import React, { memo, useMemo } from 'react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Button } from '../../components/ui/button';
import { Pin, Trash2, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

// Memoized Note Card
const NoteCard = memo(({ note, onNoteClick, onDelete, isPinned }) => (
  <div 
    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
      isPinned 
        ? 'bg-amber-50 border-amber-200 hover:border-amber-400' 
        : 'hover:shadow-sm hover:border-blue-200'
    }`}
    onClick={() => onNoteClick(note)}
    data-testid={`note-card-${note.id}`}
  >
    {isPinned && (
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 mb-2">
          <Pin className="h-4 w-4 text-amber-600" />
          <span className="text-xs text-amber-600 font-medium">Pinned</span>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6" 
          onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    )}
    {!isPinned && (
      <div className="flex items-start justify-between">
        <p className="text-sm whitespace-pre-wrap flex-1 line-clamp-4">{note.content}</p>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6" 
          onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    )}
    {isPinned && (
      <p className="text-sm whitespace-pre-wrap line-clamp-4">{note.content}</p>
    )}
    <p className="text-xs text-muted-foreground mt-2">
      {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
      {note.author && ` • by ${note.author.full_name || note.author.email}`}
    </p>
  </div>
));

NoteCard.displayName = 'NoteCard';

// Main NotesTab component
const NotesTab = memo(({ notes, onNoteClick, onDeleteNote }) => {
  const pinnedNotes = useMemo(() => notes.filter(n => n.is_pinned), [notes]);
  const unpinnedNotes = useMemo(() => notes.filter(n => !n.is_pinned), [notes]);

  if (notes.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-muted-foreground">No notes yet</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[550px]">
      <div className="p-4">
        <div className="space-y-3">
          {pinnedNotes.map(note => (
            <NoteCard 
              key={note.id} 
              note={note} 
              onNoteClick={onNoteClick}
              onDelete={onDeleteNote}
              isPinned={true}
            />
          ))}
          {unpinnedNotes.map(note => (
            <NoteCard 
              key={note.id} 
              note={note} 
              onNoteClick={onNoteClick}
              onDelete={onDeleteNote}
              isPinned={false}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
});

NotesTab.displayName = 'NotesTab';

export default NotesTab;
